import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
  Card,
  CardBody,
  Row,
  Col
} from 'reactstrap';
import { pb } from '../../helpers/pocketbase_helper';
import { createModuleLogger } from '../../utils/logger';

// Crear logger para este componente
const modalLogger = createModuleLogger('AsignarUsuarioModal');

const AsignarUsuarioModal = ({ 
  isOpen, 
  toggle, 
  tareaId, 
  onSuccess 
}) => {
  // Estados del componente
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [logs, setLogs] = useState([]);
  const [asignaciones, setAsignaciones] = useState({});
  const [tareaInfo, setTareaInfo] = useState(null);

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (isOpen && tareaId) {
      loadData();
    }
  }, [isOpen, tareaId]);

  // Limpiar estados cuando se cierra el modal
  useEffect(() => {
    if (!isOpen) {
      setError('');
      setUsuarios([]);
      setLogs([]);
      setAsignaciones({});
      setTareaInfo(null);
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      // Cargar usuarios disponibles
      const usuariosResult = await pb.collection('users').getList(1, 1000, {
        filter: 'activo = true',
        sort: 'name',
        fields: 'id,name,email,rol'
      });

      setUsuarios(usuariosResult.items);

      // Cargar información de la tarea
      const tareaResult = await pb.collection('tarea_orden').getOne(tareaId, {
        expand: 'orden_produccion,plan'
      });

      setTareaInfo(tareaResult);

      // Cargar logs de plan centro costo basado en la orden y el plan
      let logsResult = { items: [] };
      if (tareaResult.orden_produccion && tareaResult.plan) {
        // Buscar logs que pertenezcan a esta orden y plan
        logsResult = await pb.collection('log_plan_centro_costo').getList(1, 1000, {
          filter: `orden = "${tareaResult.orden_produccion}"`,
          expand: 'centro_costo,operario',
          sort: 'created'
        });
        setLogs(logsResult.items);

        // Inicializar asignaciones con los operarios actuales
        const asignacionesIniciales = {};
        logsResult.items.forEach(log => {
          asignacionesIniciales[log.id] = log.operario || '';
        });
        setAsignaciones(asignacionesIniciales);
      }

      modalLogger.info('Datos cargados exitosamente', { 
        tareaId, 
        usuariosCount: usuariosResult.items.length,
        logsCount: logsResult.items.length
      });

    } catch (err) {
      const errorMessage = 'Error al cargar los datos del modal';
      setError(errorMessage);
      modalLogger.error('Error al cargar datos', { error: err, tareaId });
    } finally {
      setLoading(false);
    }
  };

  const handleAsignacionChange = (logId, usuarioId) => {
    setAsignaciones(prev => ({
      ...prev,
      [logId]: usuarioId
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      // Actualizar cada log con su operario asignado
      const updatePromises = Object.entries(asignaciones).map(async ([logId, operarioId]) => {
        const updateData = {};
        if (operarioId) {
          updateData.operario = operarioId;
        } else {
          updateData.operario = null;
        }

        return pb.collection('log_plan_centro_costo').update(logId, updateData);
      });

      await Promise.all(updatePromises);

      modalLogger.info('Asignaciones guardadas exitosamente', { 
        tareaId, 
        asignacionesCount: Object.keys(asignaciones).length
      });

      // Notificar éxito y cerrar modal
      if (onSuccess) {
        onSuccess();
      }
      toggle();

    } catch (err) {
      const errorMessage = 'Error al guardar las asignaciones';
      setError(errorMessage);
      modalLogger.error('Error al guardar asignaciones', { error: err, tareaId });
    } finally {
      setSaving(false);
    }
  };

  const getUsuarioNombre = (usuarioId) => {
    if (!usuarioId) return 'Sin asignar';
    const usuario = usuarios.find(u => u.id === usuarioId);
    return usuario ? usuario.name || usuario.email : 'Usuario no encontrado';
  };

  const getOperarioNombre = (log) => {
    if (log.operario) {
      // Si hay operario asignado, buscar en la lista de usuarios
      const operario = usuarios.find(u => u.id === log.operario);
      return operario ? operario.name || operario.email : 'Operario no encontrado';
    }
    return 'Sin asignar';
  };

  const getCentroCostoNombre = (centroCostoId) => {
    const log = logs.find(l => l.centro_costo === centroCostoId);
    return log?.expand?.centro_costo?.nombre || 'Centro de Costo';
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Asignar Usuarios a Centros de Costo
      </ModalHeader>
      
      <ModalBody>
        {loading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Cargando datos...</p>
          </div>
        )}

        {error && (
          <Alert color="danger">
            {error}
          </Alert>
        )}

        {!loading && !error && tareaInfo && (
          <>
            {/* Información de la tarea */}
            <Card className="mb-3">
              <CardBody>
                <Row>
                  <Col md={6}>
                    <strong>Orden:</strong> #{tareaInfo.expand?.orden_produccion?.numero || 'N/A'}
                  </Col>
                  <Col md={6}>
                    <strong>Producto:</strong> {tareaInfo.expand?.orden_produccion?.nombre_producto || 'N/A'}
                  </Col>
                </Row>
                <Row className="mt-2">
                  <Col md={12}>
                    <strong>Plan:</strong> {tareaInfo.expand?.plan?.nombre || 'N/A'}
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Lista de logs para asignar usuarios */}
            <div className="mb-3">
              <h6>Centros de Costo del Plan ({logs.length} encontrados):</h6>
              {logs.length === 0 ? (
                <Alert color="info">
                  No hay centros de costo asignados a este plan.
                </Alert>
              ) : (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <Card key={log.id} className="mb-2">
                      <CardBody className="py-2">
                        <Row className="align-items-center">
                          <Col md={4}>
                            <strong>
                              {getCentroCostoNombre(log.centro_costo)}
                            </strong>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-0">
                              {log.operario && log.inicio ? (
                                <Input
                                  type="text"
                                  value={getOperarioNombre(log)}
                                  readOnly
                                  size="sm"
                                  className="bg-light"
                                />
                              ) : (
                                <Input
                                  type="select"
                                  value={asignaciones[log.id] || ''}
                                  onChange={(e) => handleAsignacionChange(log.id, e.target.value)}
                                  size="sm"
                                >
                                  <option value="">Sin asignar</option>
                                  {usuarios.map((usuario) => (
                                    <option key={usuario.id} value={usuario.id}>
                                      {usuario.name || usuario.email}
                                    </option>
                                  ))}
                                </Input>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={2}>
                            <small className="text-muted">
                              {log.operario ? 'Asignado' : 'Pendiente'}
                            </small>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={saving}>
          Cancelar
        </Button>
        <Button 
          color="primary" 
          onClick={handleSave} 
          disabled={saving || loading}
        >
          {saving ? (
            <>
              <Spinner size="sm" className="me-2" />
              Guardando...
            </>
          ) : (
            'Guardar Asignaciones'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AsignarUsuarioModal;
